<script setup>
  import { computed } from 'vue'

  const emit = defineEmits(['update:modelValue', 'change'])

  const props = defineProps({
    modelValue: {
      type: String,
      required: false,
    },
    tabs: {
      type: Array,
      required: true,
    },
    alignment: {
      type: String,
      default: 'center',
    },
    fluid: {
      type: Boolean,
      default: false,
    },
  })

  const activeTab = computed({
    get: () => props.modelValue,
    set: value => {
      emit('update:modelValue', value)
      emit('change', value)
    },
  })

  function selectTab(tabValue) {
    if (props.tabs.find(t => t.value === tabValue).disabled) {
      return
    }

    activeTab.value = tabValue
  }

  const hiddenFilteredTabs = computed(() => props.tabs.filter(tab => !tab.hidden))
</script>

<template>
  <div class="custom-tabs" data-test-id="custom-tabs">
    <div
      class="tab-list"
      :class="{
        [alignment]: true,
        fluid: fluid,
      }"
    >
      <button
        v-for="tab of hiddenFilteredTabs"
        :key="tab.value"
        :aria-label="tab.label"
        :class="{ active: activeTab === tab.value || tab.active, disabled: tab.disabled }"
        :data-test-id="'tab-' + tab.value"
        type="button"
        @click="selectTab(tab.value)"
      >
        <span v-if="tab.icon" :class="tab.icon"></span>
        <slot :name="`tab-${tab.value}`" :tab="tab">
          {{ tab.label }}
        </slot>
      </button>

      <slot name="tab-actions" />
    </div>
    <div class="tab-content" data-test-id="tab-content">
      <slot></slot>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .custom-tabs {
    display: flex;
    overflow-x: auto;
    width: 100%;
  }

  .tab-list {
    display: flex;
    list-style: none;
    padding: 0;
    margin: 0;
    border-bottom: 1px solid light-dark(#ccc, #444);
    align-items: center;
    width: 100%;
    margin-right: 4px;

    @include scrollbar();

    &.center {
      justify-content: center;
    }

    &.left {
      justify-content: flex-start;
    }

    &.fluid {
      width: 100%;
      justify-content: space-between;

      li {
        flex: 1;
        justify-content: center;
      }
    }
  }

  .tab-list button {
    @include resetButton;
    @include focusRing;

    padding: 10px 15px;
    cursor: pointer;
    border-bottom: 3px solid transparent;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .tab-list button.active {
    border-bottom: 3px solid light-dark(#4c8bf5, #4c8bf5);
    outline: none;
  }

  .tab-list button.disabled {
    color: light-dark(#a0a0a0, #a0a0a0);
  }

  .tab-list button:focus {
    outline: none;
  }
</style>
